import { memo } from 'react';
import { Tooltip, Text } from '@library';
import { StockStatuses } from '../types';
import notEmpty from '../../core/util/notEmpty';
import getClassNamesFromStockStatuses from '../util/getClassNamesFromStockStatuses';
import shouldNotShowAvailabilityForBranch from '../util/shouldNotShowAvailabilityForBranch';
import useRestrictionMessages from '../queries/useRestrictionMessages';
import ProductStockLoadingIndicator from './ProductStockLoadingIndicator';
import styles from './ProductStockIndicator.module.scss';
import PersistedProductStockIndicatorContent from './PersistedProductStockIndicatorContent';
import StockIndicatorContent from './StockIndicatorContent';

const useRestrictionMessage = (restrictionId?: string) => {
    const { messages: restrictionMessages } =
        useRestrictionMessages(!restrictionId);

    return restrictionMessages?.find(item => item.id === restrictionId)
        ?.message;
};

const ProductStockIndicatorContent = ({
    restrictionId,
    loading,
    lineIndicator = false,
    statuses,
    status,
    messageStyle,
    stockMessage,
    isMixed,
    location,
    branchName,
    hideBranchesWithoutAvailability = false,
    hideBranchCode = false,
    isSecondaryBranch = false,
}: {
    restrictionId?: string;
    loading: boolean;
    lineIndicator?: boolean;
    statuses?: StockStatuses[];
    status?: StockStatuses;
    isMixed?: boolean;
    messageStyle: 'left' | 'right' | 'tooltip' | 'none';
    stockMessage: string;
    location: string;
    branchName: string;
    hideBranchesWithoutAvailability?: boolean;
    hideBranchCode?: boolean;
    isSecondaryBranch?: boolean;
}) => {
    const restrictionMessage = useRestrictionMessage(restrictionId);

    const dataTestClass = notEmpty(statuses)
        ? 'stockIndicator'
        : 'mixedStockIndicator';

    const singleStatus = statuses?.[0] ?? status ?? StockStatuses.UNAVAILABLE;

    const dataTestState = [
        StockStatuses.AVAILABLE,
        StockStatuses.AVAILABLE_IN_FUTURE,
        StockStatuses.ON_DEMAND,
        StockStatuses.UNKNOWN,
    ].includes(singleStatus)
        ? 'available'
        : 'unavailable';

    if (loading) {
        if (isSecondaryBranch) {
            return null;
        }

        return (
            <ProductStockLoadingIndicator
                square={hideBranchCode}
                data-test-class={dataTestClass}
                data-test-state="loading"
                lineIndicator={lineIndicator}
            />
        );
    }

    if (
        shouldNotShowAvailabilityForBranch(
            hideBranchesWithoutAvailability,
            status,
            statuses
        )
    ) {
        return null;
    }

    const classNames = getClassNamesFromStockStatuses({
        status,
        statuses,
        isMixed,
        lineIndicator,
        styles,
        square: hideBranchCode,
    });

    if (messageStyle === 'tooltip') {
        return (
            <Tooltip
                disabled={!stockMessage}
                content={
                    <div className={styles.tooltip}>
                        <Text primary>
                            {restrictionMessage
                                ? restrictionMessage
                                : stockMessage}
                        </Text>
                    </div>
                }
                placement="top"
                showArrow
            >
                <div
                    className={classNames}
                    data-test-class={dataTestClass}
                    data-test-state={dataTestState}
                >
                    <StockIndicatorContent
                        lineIndicator={lineIndicator}
                        hideBranchCode={hideBranchCode}
                        hasMoreInfo={!!restrictionMessage}
                        location={location}
                    />
                </div>
            </Tooltip>
        );
    }

    if (['left', 'right'].includes(messageStyle)) {
        return (
            <PersistedProductStockIndicatorContent
                restrictionMessage={restrictionMessage}
                classNames={classNames}
                messageStyle={messageStyle}
                stockMessage={stockMessage}
                branchName={branchName}
                dataTestClass={dataTestClass}
                dataTestState={dataTestState}
                availabilityIndicatorContent={
                    <StockIndicatorContent
                        lineIndicator={lineIndicator}
                        hideBranchCode={hideBranchCode}
                        hasMoreInfo={!!restrictionMessage}
                        location={location}
                    />
                }
            />
        );
    }

    return (
        <div className={classNames}>
            <StockIndicatorContent
                lineIndicator={lineIndicator}
                hideBranchCode={hideBranchCode}
                hasMoreInfo={!!restrictionMessage}
                location={location}
            />
        </div>
    );
};

export default memo(ProductStockIndicatorContent);
